import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { Editor } from '@tiptap/react';
import type { EditorView } from '@tiptap/pm/view';
import clsx from 'clsx';
import { useEmailSnippets } from '../../hooks/useEmailSnippets';
import { filterSnippets, snippetInsertHtml, type SnippetMatch } from '../../utils/snippets';

/**
 * The mail editor's "/" snippet menu (see docs/EMAIL_SNIPPETS.md).
 *
 * Everything here hangs off the editor itself — the query is read from the text before the caret
 * and the keys are taken in ProseMirror's `handleKeyDown` — so the menu exists only while the
 * editor has focus and never competes with the page's own `document` shortcuts.
 *
 * `/` only opens the menu at the start of a word, so a URL or a date like `12/05` is left alone,
 * and the query stops at the next space: typing a space closes the menu.
 */
const SLASH_QUERY = /(?:^|\s)\/([^\s/]*)$/;

/** Gap between the caret and the menu, and the margin it keeps from the viewport edges. */
const GAP = 4;
const MARGIN = 8;
const MENU_WIDTH = 340;
/** Above the modal backdrop's 9999, since the editors that use this live inside modals. */
const Z_INDEX = 10050;

interface SlashContext {
  /** Document position of the `/` itself. */
  from: number;
  /** Caret position — the end of the range the snippet replaces. */
  to: number;
  query: string;
}

interface Placement {
  left: number;
  top: number;
}

export interface SnippetMenu {
  /** Pass to `editorProps.handleKeyDown`. */
  handleKeyDown: (view: EditorView, event: KeyboardEvent) => boolean;
  /** Call whenever the document or the selection moves, and on focus. */
  sync: (editor: Editor) => void;
  /** Call on blur. */
  close: () => void;
  /** Render inside the editor's wrapper; portals itself to the body. */
  menu: ReactNode;
}

export function useSnippetMenu(enabled: boolean): SnippetMenu {
  const snippets = useEmailSnippets(enabled);
  const [slash, setSlash] = useState<SlashContext | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  // Enter picks a snippet only once the user has narrowed or browsed the list. Straight after the
  // slash it stays the editor's own key, which is what leaves a lone "/" in the text.
  const [navigated, setNavigated] = useState(false);
  const [placement, setPlacement] = useState<Placement | null>(null);
  const editorRef = useRef<Editor | null>(null);
  /** The slash position the user dismissed, so the menu does not spring back on the next keystroke. */
  const dismissed = useRef<number | null>(null);
  /** Mirror of `slash`, so `sync` can compare against the live value without depending on it. */
  const slashRef = useRef<SlashContext | null>(null);

  const matches = useMemo(
    () => (slash ? filterSnippets(snippets, slash.query) : []),
    [slash, snippets]
  );
  const isOpen = !!slash && matches.length > 0;

  const close = useCallback(() => {
    slashRef.current = null;
    setSlash(null);
    setPlacement(null);
    setNavigated(false);
  }, []);

  const dismiss = useCallback(
    (from: number) => {
      dismissed.current = from;
      close();
    },
    [close]
  );

  const sync = useCallback(
    (editor: Editor) => {
      editorRef.current = editor;
      if (!enabled) return;
      const { selection } = editor.state;
      const { $from, empty } = selection;
      if (!empty || !editor.isFocused || !$from.parent.isTextblock) {
        close();
        return;
      }
      const textBefore = $from.parent.textBetween(0, $from.parentOffset, undefined, ' ');
      const match = SLASH_QUERY.exec(textBefore);
      if (!match) {
        // The caret is no longer on a slash run, so an earlier dismissal has nothing left to
        // suppress: the next slash here starts over.
        dismissed.current = null;
        close();
        return;
      }
      const query = match[1];
      const from = $from.pos - query.length - 1;
      if (dismissed.current === from) {
        close();
        return;
      }
      const prev = slashRef.current;
      if (prev && prev.from === from && prev.to === $from.pos && prev.query === query) return;
      // A new slash starts fresh; a changed query re-ranks the list, so the highlight goes back
      // to the best match either way.
      if (!prev || prev.from !== from) setNavigated(false);
      if (!prev || prev.from !== from || prev.query !== query) setActiveIndex(0);
      slashRef.current = { from, to: $from.pos, query };
      setSlash(slashRef.current);
    },
    [close, enabled]
  );

  // The range to replace is passed in rather than read from state: this callback has to stay
  // stable for the key handler below, which is captured once when the editor is created.
  const insert = useCallback(
    (match: SnippetMatch, ctx: SlashContext) => {
      const editor = editorRef.current;
      if (!editor) return;
      close();
      dismissed.current = null;
      editor
        .chain()
        .focus()
        .insertContentAt({ from: ctx.from, to: ctx.to }, snippetInsertHtml(match.snippet.html))
        .run();
    },
    [close]
  );

  // Menu state is read from a ref: `editorProps` is captured when the editor is created, so the
  // handler below outlives every render that changes that state.
  const live = useRef({ enabled, isOpen, matches, activeIndex, navigated, slash });
  useEffect(() => {
    live.current = { enabled, isOpen, matches, activeIndex, navigated, slash };
  }, [enabled, isOpen, matches, activeIndex, navigated, slash]);

  const handleKeyDown = useCallback(
    (_view: EditorView, event: KeyboardEvent): boolean => {
      const {
        enabled: on,
        isOpen: open,
        matches: current,
        activeIndex: index,
        navigated: browsed,
        slash: ctx,
      } = live.current;
      if (!on) return false;
      // A fresh slash always gets a menu, even at a position dismissed earlier.
      if (event.key === '/') {
        dismissed.current = null;
        return false;
      }
      if (!open || !ctx || current.length === 0) return false;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setNavigated(true);
          setActiveIndex((i) => (i + 1) % current.length);
          return true;
        case 'ArrowUp':
          event.preventDefault();
          setNavigated(true);
          setActiveIndex((i) => (i - 1 + current.length) % current.length);
          return true;
        case 'Enter':
        case 'Tab':
          if (!ctx.query && !browsed) {
            // Straight after the slash: leave the key to the editor and keep the slash as typed.
            dismiss(ctx.from);
            return false;
          }
          event.preventDefault();
          insert(current[Math.min(index, current.length - 1)], ctx);
          return true;
        case 'Escape':
          event.preventDefault();
          // The editors live in modals that close on Escape from a `document` listener.
          event.stopPropagation();
          dismiss(ctx.from);
          return true;
        default:
          return false;
      }
    },
    [dismiss, insert]
  );

  // Place the menu under the slash, flipping above it when the viewport has no room below.
  useLayoutEffect(() => {
    const editor = editorRef.current;
    if (!isOpen || !slash || !editor) return;
    const place = () => {
      let caret;
      try {
        caret = editor.view.coordsAtPos(slash.from);
      } catch {
        return; // position no longer in the document
      }
      const height = Math.min(240, matches.length * 34 + 8);
      const below = caret.bottom + GAP + height < window.innerHeight - MARGIN;
      setPlacement({
        left: Math.min(Math.max(caret.left, MARGIN), Math.max(window.innerWidth - MENU_WIDTH - MARGIN, MARGIN)),
        top: below ? caret.bottom + GAP : Math.max(caret.top - GAP - height, MARGIN),
      });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [isOpen, slash, matches.length]);

  // Keep the highlight visible when the arrows walk past the menu's bottom edge.
  const activeRef = useRef<HTMLButtonElement | null>(null);
  useLayoutEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, isOpen, placement]);

  const menu =
    isOpen && placement
      ? createPortal(
          <div
            className="fixed w-[340px] max-h-60 overflow-y-auto rounded-lg border border-dark-border bg-dark-surface shadow-2xl py-1"
            style={{ left: placement.left, top: placement.top, zIndex: Z_INDEX }}
            role="listbox"
          >
            {matches.map((match, i) => (
              <button
                key={match.index}
                ref={i === activeIndex ? activeRef : null}
                type="button"
                role="option"
                aria-selected={i === activeIndex}
                // Keep the editor focused: a blur would retire the menu before the click lands.
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActiveIndex(i)}
                onClick={() => insert(match, slash)}
                className={clsx(
                  'w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm',
                  i === activeIndex
                    ? 'bg-primary-600/20 text-dark-text-primary'
                    : 'text-dark-text-secondary hover:bg-dark-elevated'
                )}
              >
                {match.snippet.tag && (
                  <span className="shrink-0 px-1.5 py-0.5 rounded-sm text-[10px] uppercase tracking-wide bg-dark-elevated border border-dark-border text-dark-text-muted">
                    {match.snippet.tag}
                  </span>
                )}
                <span className="truncate">{match.text}</span>
              </button>
            ))}
          </div>,
          document.body
        )
      : null;

  return { handleKeyDown, sync, close, menu };
}
